import { useEffect, useState } from "react";
import { useDeviceStore } from "../../store/useDeviceStore";
import { useGetAllDevices } from "../../queries/admin";
import type { Device } from "../../types/device";

type StatusTone = "online" | "offline";

const TONE_STYLES: Record<StatusTone, { container: string; dot: string }> = {
  online: {
    container:
      "border-green-200 dark:border-green-900/40 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400",
    dot: "bg-green-500 animate-pulse",
  },
  offline: {
    container:
      "border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400",
    dot: "bg-red-500",
  },
};

// Mirrors the Online/Offline badge used in the device list so the status label
// stays consistent everywhere: connectivity only (isOnline, computed
// server-side), never a registration state.
function getStatusPresentation(
  isOnline?: boolean,
): { label: string; tone: StatusTone } {
  return isOnline
    ? { label: "Online", tone: "online" }
    : { label: "Offline", tone: "offline" };
}

// Relative "Updated X ago" label for the last reading received from the device.
function formatUpdatedAgo(dateStr?: string | Date | null): string {
  if (!dateStr) return "No data received yet";
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return "No data received yet";
  const diffMins = Math.floor((Date.now() - then) / 60000);
  if (diffMins < 1) return "Updated just now";
  if (diffMins < 60) return `Updated ${diffMins} min${diffMins !== 1 ? "s" : ""} ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Updated ${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `Updated ${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
}

interface DeviceStatusBarProps {
  // When provided (device detail page), the banner reflects this device
  // directly. When omitted (dashboard navbar), it follows the single selected
  // device from the store.
  device?: Device | null;
  // Fallback timestamp (e.g. the last reading recorded in the DB) used when the
  // device record has no lastDataReceivedAt yet.
  fallbackTimestamp?: string | null;
}

// Online/offline banner shown for a single focused device — the dashboard
// navbar (selected device) and the device detail page. Reflects the same
// isOnline signal (data received within 3x the device's data rate) used by the
// device list/detail badges, polled continuously via useGetAllDevices, with a
// secondary line showing when the last reading arrived.
export default function DeviceStatusBar({
  device: deviceProp,
  fallbackTimestamp,
}: DeviceStatusBarProps = {}) {
  const { selectedDeviceIds, selectedDeviceId, selectedDeviceName } = useDeviceStore();
  const { data: devices } = useGetAllDevices();

  // Re-render periodically so the relative "Updated X ago" stays current
  // between data-rate polls.
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // The detail page passes the device explicitly; the dashboard navbar derives
  // it from the store's single selection (and hides for "All"/multi-select).
  let device = deviceProp ?? null;
  let name = device?.name;
  if (!deviceProp) {
    if (selectedDeviceIds.length !== 1 || !selectedDeviceId) return null;
    device = devices?.find((d: any) => d.id === selectedDeviceId) ?? null;
    name = device?.name || selectedDeviceName;
  }
  if (!name) return null;

  const { label, tone } = getStatusPresentation(device?.isOnline);
  const styles = TONE_STYLES[tone];
  const updatedAgo = formatUpdatedAgo(device?.lastDataReceivedAt ?? fallbackTimestamp);

  return (
    <div
      title={`${name} — ${label} · ${updatedAgo}`}
      className={`flex flex-col gap-0.5 shrink-0 rounded-lg border px-3 py-1.5 ${styles.container}`}
    >
      <div className="flex items-center gap-2 text-sm font-medium">
        <span className={`h-1.5 w-1.5 rounded-full ${styles.dot}`} />
        <span className="max-w-[160px] truncate">{name}</span>
        <span className="text-xs opacity-75">{label}</span>
      </div>
      <span className="text-[11px] font-normal leading-tight text-gray-500 dark:text-gray-400">
        {updatedAgo}
      </span>
    </div>
  );
}
